import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, Validators } from '@angular/forms';
import { TransactionAllocation } from '@principle-theorem/principle-core';
import {
  IInvoice,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  isSameRef,
  WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

export interface ICreatePractitionerLimitOptions {
  /**
   * An optional base limit that will be applied as a ceiling.
   * The final limit will be the minimum of this and the practitioner's
   * allocatable amount.
   */
  baseLimit?: number;

  /**
   * When amending an existing transaction, pass the current transaction's
   * amount and attributedTo so that we can add it back to the available
   * allocation for the same practitioner.
   */
  currentTransaction?: {
    amount: number;
    attributedTo?: DocumentReference<IStaffer>;
  };
}

/**
 * Creates an observable that emits the maximum allocatable amount for a
 * practitioner on an invoice.
 *
 * This is a reactive wrapper around
 * `TransactionAllocation.getMaxAllocatableForPractitioner` that automatically
 * recalculates when the attributedTo value changes.
 *
 * @param attributedTo$ - An observable emitting the currently selected
 *   practitioner reference (or undefined for no selection)
 * @param invoice - The invoice to calculate allocations against
 * @param options - Optional configuration for base limits and current
 *   transaction handling
 * @returns An observable emitting the maximum allocatable amount
 */
export function createPractitionerLimit$(
  attributedTo$: Observable<DocumentReference<IStaffer> | undefined>,
  invoice: WithRef<IInvoice>,
  options?: ICreatePractitionerLimitOptions
): Observable<number> {
  return attributedTo$.pipe(
    switchMap(async (attributedTo) => {
      const maxAllocatableForPractitioner =
        await TransactionAllocation.getMaxAllocatableForPractitioner(
          invoice,
          attributedTo ?? undefined
        );

      // When amending a transaction, add back the current transaction's amount
      // if it's attributed to the same practitioner
      const currentTransactionAmount =
        options?.currentTransaction?.attributedTo &&
        attributedTo &&
        isSameRef(options.currentTransaction.attributedTo, attributedTo)
          ? options.currentTransaction.amount
          : 0;

      const practitionerLimit =
        maxAllocatableForPractitioner + currentTransactionAmount;

      return options?.baseLimit !== undefined
        ? Math.min(options.baseLimit, practitionerLimit)
        : practitionerLimit;
    })
  );
}

export interface IBindLimitToAmountControlOptions {
  /**
   * Whether the amount field is required. Defaults to true.
   */
  required?: boolean;
}

/**
 * Binds a limit$ observable to an amount form control, automatically:
 * - Clamping the control's value if it exceeds the new limit
 * - Setting appropriate validators (required + max)
 * - Updating validity
 *
 * @param limit$ - An observable emitting the maximum allowed amount
 * @param amountControl - The form control to bind to
 * @param destroyRef - Angular DestroyRef for automatic cleanup
 * @param options - Optional configuration
 */
export function bindLimitToAmountControl(
  limit$: Observable<number>,
  amountControl: AbstractControl<number | null | undefined>,
  destroyRef: DestroyRef,
  options?: IBindLimitToAmountControlOptions
): void {
  const isRequired = options?.required ?? true;

  limit$.pipe(takeUntilDestroyed(destroyRef)).subscribe((limit) => {
    const currentValue = amountControl.value ?? limit;
    if (currentValue > limit) {
      amountControl.setValue(limit);
    }

    const validators = compact([
      isRequired ? Validators.required : undefined,
      Validators.max(limit),
    ]);
    amountControl.setValidators(validators);
    amountControl.updateValueAndValidity();
  });
}
